import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Divider,
  Fade,
  Flex,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
} from "@chakra-ui/react";
import { H2 } from "../../../../components/text/headings";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGetMultiYearEmissions, useGetTotalEmissions } from "../../../../hooks/query/emissions-insight/emissions-insights-query";
import LocalSpinner from "../../../../components/LocalSpinner";
import { CaretDown, ChartDonut, Check, ListBullets, Table } from "@phosphor-icons/react";
import { useGetDimensionFilterOptions } from "../../../../hooks/query/dimensions";
import { dimensionsKeys } from "../../../../utils";
import SummaryCards from "./SummaryCards";
import KpiCharts from "./KpiCharts";
import EmissionsTrends from "./EmissionsTrends";
import { useSearchParams } from "react-router-dom";
import ScopeOneDetail from "./details/ScopeOneDetail";
import ScopeTwoDetail from "./details/ScopeTwoDetail";
import ScopeThreeDetail from "./details/ScopeThreeDetail";
import ScopeThreeCategoryDetail from "./details/ScopeThreeCategoryDetail";
import EmissionsBreakdown from "./EmissionsBreakdown";
import SecrConsumptionDashboard from "./Consumption/SecrConsumptionDashboard";
import QuarterlyEmissionsTable from "./QuarterlyEmissionsTable";

// A year counts as "having data" if any of the headline totals come back as
// a non-zero number. 404s are filtered out before this helper is called.
const hasAnyEmissions = (data) => {
  if (!data) return false;
  const totals = [
    data.scope1Total,
    data.scope2LocationTotal,
    data.scope2MarketTotal,
    data.scope3Total,
    data.scope1AndScope2LocationTotal,
    data.scope1AndScope2MarketTotal,
  ];
  return totals.some((v) => typeof v === "number" && v > 0);
};

const FootPrint = () => {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const detailView = searchParams.get("detailView");

  // Initialize from URL params if present; otherwise start on the current
  // calendar year as a placeholder. Either way, the probe below will override
  // if the chosen year turns out to have no data.
  const urlYear = Number(searchParams.get("year")) || null;
  const initialYear = urlYear || new Date().getFullYear();
  // Quarter is optional; a missing/"ALL" param means the whole year.
  const initialQuarter = searchParams.get("quarter") || null;
  const initialDimension = (() => {
    const dimParam = searchParams.get("dimension");
    if (!dimParam) return {};
    return dimParam.split("&").reduce((acc, pair) => {
      const [key, values] = pair.split("=");
      acc[key] = values ? values.split(",") : [];
      return acc;
    }, {});
  })();

  // States
  const [year, setYear] = useState(initialYear);
  const [selectedViewType, setSelectedViewType] = useState("summary");
  const [dimension, setDimension] = useState(initialDimension);
  const [autoYearResolved, setAutoYearResolved] = useState(false);
  // Quarter filter: null/"ALL" = whole year, otherwise "Q1".."Q4". Synced to
  // the ?quarter= URL param and threaded into the insights data hooks below.
  const [quarter, setQuarter] = useState(initialQuarter);

  // Scroll the Emissions Breakdown section to the top of the layout's
  // scroll area. Manual smooth animation because Chakra's accordion-button
  // focus side-effects overwrite browser-native smooth scrolls; an rAF
  // loop that writes scrollTop every frame outlasts them. Same scroll is
  // triggered both on accordion open and on inner-tab switch (Scope /
  // Dimension / Type) so the section's contents stay visible after a
  // long Scope tree drill-down.
  const breakdownRef = useRef(null);
  const scrollBreakdownIntoView = useCallback(() => {
    const el = breakdownRef.current;
    if (!el) return;
    let scroller = null;
    let node = el.parentElement;
    while (node && node !== document.documentElement) {
      const cs = window.getComputedStyle(node);
      const oy = cs.overflowY;
      const scrollable = oy === "auto" || oy === "scroll" || oy === "overlay";
      if (scrollable && node.scrollHeight - node.clientHeight > 1) {
        scroller = node;
        break;
      }
      node = node.parentElement;
    }
    if (!scroller) scroller = document.getElementById("layoutContainer");
    if (!scroller) return;

    const elRect0 = el.getBoundingClientRect();
    const scRect0 = scroller.getBoundingClientRect();
    const start = scroller.scrollTop;
    const target = Math.max(0, start + (elRect0.top - scRect0.top) - 8);
    if (Math.abs(target - start) < 1) return;

    const duration = 350;
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      const elapsed = now - t0;
      const t = Math.min(1, elapsed / duration);
      scroller.scrollTop = start + (target - start) * ease(t);
      if (t < 1) requestAnimationFrame(step);
    };
    setTimeout(() => requestAnimationFrame(step), 0);
  }, []);
  const handleBreakdownAccordionChange = (expandedIndices) => {
    const isOpen = Array.isArray(expandedIndices)
      ? expandedIndices.length > 0
      : expandedIndices >= 0;
    if (isOpen) scrollBreakdownIntoView();
  };

  // Probe the last 5 years (descending) on first mount to find the latest
  // year with data. Runs once — after resolution we lock and never re-probe,
  // so manual year selections aren't overridden. If the initial year (from
  // URL or default) has data we keep it; otherwise we jump to the latest
  // year that does.
  const probeYears = useMemo(() => {
    const now = new Date().getFullYear();
    // 2 years forward + current + 5 years back (descending)
    return Array.from({ length: 8 }, (_, i) => now + 2 - i);
  }, []);

  const probeQueries = useGetMultiYearEmissions(probeYears, "", {
    enabled: !autoYearResolved,
    retry: false,
    cacheTime: 60_000,
  });

  useEffect(() => {
    if (autoYearResolved) return;
    const allSettled = probeQueries.every((q) => !q.isLoading);
    if (!allSettled) return;
    const initialHasData = probeQueries.some(
      (q, i) => probeYears[i] === initialYear && q.status === "success" && hasAnyEmissions(q.data)
    );
    if (!initialHasData) {
      // Pick the latest year (probeYears is descending) that has data.
      const hit = probeQueries.find(
        (q) => q.status === "success" && q.data && hasAnyEmissions(q.data)
      );
      if (hit) {
        const idx = probeQueries.indexOf(hit);
        setYear(probeYears[idx]);
      }
    }
    setAutoYearResolved(true);
  }, [probeQueries, probeYears, initialYear, autoYearResolved]);

  // Update URL params when year, quarter, or dimension changes
  useEffect(() => {
    const params = searchParams;
    params.set("year", year);
    if (quarter && quarter !== "ALL") {
      params.set("quarter", quarter);
    } else {
      params.delete("quarter");
    }
    if (Object.keys(dimension).length > 0) {
      params.set(
        "dimension",
        Object.entries(dimension)
          .filter(([_, ids]) => ids.length > 0)
          .map(([key, ids]) => `${key}=${ids.join(",")}`)
          .join("&")
      );
    }
    setSearchParams(params);
  }, [year, quarter, dimension, searchParams, setSearchParams]);

  const getDimensionParams = () => {
    return Object.entries(dimension)
      .filter(([_, ids]) => ids.length > 0) // Filter out dimensions with no selected items
      .map(([key, ids]) => `${key}=${ids.join(",")}`) // Format each dimension as key=value1,value2
      .join("&"); // Join all dimensions with '&'
  };

  const handleEmissionsError = (error, year) => {
    // Suppress the "no report for year" warning while the auto-probe is
    // resolving — it will either confirm the year or jump to a better one,
    // and a transient warning toast during that flicker is just noise.
    if (!autoYearResolved) return;
    if (error?.response?.status === 404) {
      toast({
        title: "Report not available for year " + year,
        description: "The emissions report for this year is not available.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Error fetching current year emissions data",
        // Surface the backend's structured message (e.g. "Cannot find
        // conversion factor for the country: …") instead of axios's generic
        // "Request failed with status code 500".
        description: error?.response?.data?.parameters?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const { data: currentYearData, isFetching: isFetchingCurrentYear } = useGetTotalEmissions(
    year,
    getDimensionParams(),
    {
      onError: (error) => handleEmissionsError(error, year),
      cacheTime: 60, // 1 minute
    },
    quarter
  );

  const { data: previousYearData, isFetching: isFetchingPreviousYear } = useGetTotalEmissions(
    year - 1,
    getDimensionParams(),
    {
      onError: (error) => handleEmissionsError(error, year - 1),
      cacheTime: 60, // 1 minute
    },
    quarter
  );

  return (
    <Flex direction="column" gap={4}>
      {detailView === "scopeOne" && (
        <ScopeOneDetail
          currentYearData={currentYearData}
          previousYearData={previousYearData}
          isFetchingCurrentYear={isFetchingCurrentYear}
          isFetchingPreviousYear={isFetchingPreviousYear}
          year={year}
          setYear={setYear}
          quarter={quarter}
          setQuarter={setQuarter}
          dimension={dimension}
          setDimension={setDimension}
          dimensionParams={getDimensionParams()}
        />
      )}
      {detailView === "scopeTwo" && (
        <ScopeTwoDetail
          currentYearData={currentYearData}
          previousYearData={previousYearData}
          isFetchingCurrentYear={isFetchingCurrentYear}
          isFetchingPreviousYear={isFetchingPreviousYear}
          year={year}
          setYear={setYear}
          quarter={quarter}
          setQuarter={setQuarter}
          dimension={dimension}
          setDimension={setDimension}
          dimensionParams={getDimensionParams()}
        />
      )}
      {detailView === "scopeThree" && (
        <ScopeThreeDetail
          year={year}
          setYear={setYear}
          quarter={quarter}
          setQuarter={setQuarter}
          dimension={dimension}
          setDimension={setDimension}
          dimensionParams={getDimensionParams()}
        />
      )}
      {detailView === "scopeThreeCategory" && (
        <ScopeThreeCategoryDetail
          year={year}
          setYear={setYear}
          quarter={quarter}
          setQuarter={setQuarter}
          dimension={dimension}
          setDimension={setDimension}
          dimensionParams={getDimensionParams()}
        />
      )}

      {!detailView && (
        <Tabs
          variant="primary"
          isLazy
          // Tab is URL-addressable (?tab=consumption) so other pages — e.g.
          // the Scope 2 drill-down's onsite-generation note — can deep-link
          // straight to the Consumption view.
          index={searchParams.get("tab") === "consumption" ? 1 : 0}
          onChange={(i) => {
            const params = new URLSearchParams(searchParams);
            if (i === 1) params.set("tab", "consumption");
            else params.delete("tab");
            setSearchParams(params);
          }}
        >
          <TabList>
            <Tab>Total Emissions</Tab>
            <Tab>Consumption</Tab>
          </TabList>
          <Text maxW="75%" color="gray.500" fontSize="body.m" mt={3}>
            Monitor and track key indicators and trends on sustainability performance across your organisation and supply
            chain all in one place.
          </Text>
          <TabPanels>
            <TabPanel px={0}>
              <Flex direction="column" gap={4}>
                <Flex justifyContent="space-between" align="center" mt={2}>
                  <Flex gap={2}>
                    <DimensionDropdown selected={dimension} setSelected={setDimension} />
                    <YearDropdown setYear={setYear} selectedYear={year} />
                    <QuarterDropdown
                      setQuarter={setQuarter}
                      selectedQuarter={quarter}
                      // The table always shows the full year — quarters ARE
                      // its columns, so the filter has no effect there.
                      isDisabled={selectedViewType === "table"}
                      disabledReason="The table always shows all quarters"
                    />
                  </Flex>
                  <ToggleButton
                    options={[
                      { label: "Summary", value: "summary", icon: <ListBullets size={18} /> },
                      { label: "Visual", value: "visual", icon: <ChartDonut size={18} /> },
                      { label: "Table", value: "table", icon: <Table size={18} /> },
                    ]}
                    selected={selectedViewType}
                    setSelected={setSelectedViewType}
                  />
                </Flex>
                <Fade in={selectedViewType === "summary"} unmountOnExit>
                  <SummaryCards
                    currentYearData={currentYearData}
                    previousYearData={previousYearData}
                    isFetchingCurrentYear={isFetchingCurrentYear}
                    isFetchingPreviousYear={isFetchingPreviousYear}
                    selectedYear={year}
                    quarter={quarter}
                    dimensionActive={getDimensionParams().length > 0}
                  />
                </Fade>
                <Fade in={selectedViewType === "table"} unmountOnExit>
                  {/* Quarter columns replace the page's quarter filter here — the
                      table always shows the full year, ignoring any Q selection. */}
                  <QuarterlyEmissionsTable year={year} dimensionParams={getDimensionParams()} />
                </Fade>
                <Fade in={selectedViewType === "visual"} unmountOnExit>
                  <KpiCharts
                    currentYearData={currentYearData}
                    previousYearData={previousYearData}
                    isFetchingCurrentYear={isFetchingCurrentYear}
                    isFetchingPreviousYear={isFetchingPreviousYear}
                    selectedYear={year}
                    setYear={setYear}
                  />
                </Fade>
                <EmissionsTrends dimensionParams={getDimensionParams()} selectedYear={year} quarter={quarter} />
                <Box ref={breakdownRef}>
                  <Accordion allowToggle onChange={handleBreakdownAccordionChange}>
                    <AccordionItem borderTop="none" borderBottom="1px solid" borderColor="gray.200">
                      <AccordionButton>
                        <H2 fontSize="body.2xl">Emissions Breakdown</H2>
                        <AccordionIcon mr={0} ml="auto" />
                      </AccordionButton>
                      <AccordionPanel mt={4}>
                        <EmissionsBreakdown
                          currentYearData={currentYearData}
                          isFetchingCurrentYear={isFetchingCurrentYear}
                          selectedYear={year}
                          quarter={quarter}
                          onTabChange={scrollBreakdownIntoView}
                        />
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </Box>
              </Flex>
            </TabPanel>
            <TabPanel px={0}>
              <SecrConsumptionDashboard
                year={year}
                setYear={setYear}
                dimension={dimension}
                setDimension={setDimension}
                quarter={quarter}
                setQuarter={setQuarter}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </Flex>
  );
};

const ToggleButton = ({ options, selected, setSelected }) => {
  return (
    <Flex bg="gray.100" borderRadius="full" p="3px">
      {options.map((option) => {
        const active = selected === option.value;
        return (
          <Button
            key={option.value}
            size="sm"
            borderRadius="full"
            bg={active ? "pureBlack" : "transparent"}
            color={active ? "white" : "gray.500"}
            fontWeight={active ? "600" : "normal"}
            rightIcon={option.icon}
            onClick={() => setSelected(option.value)}
            _hover={{ bg: active ? "pureBlack" : "gray.200" }}
            px={5}
          >
            {option.label || option.value}
          </Button>
        );
      })}
    </Flex>
  );
};

export const QuarterDropdown = ({ setQuarter, selectedQuarter, isDisabled = false, disabledReason }) => {
  const quarters = ["Q1", "Q2", "Q3", "Q4"];

  return (
    <Menu>
      <MenuButton
        as={Button}
        size="sm"
        borderRadius="full"
        border="1px solid"
        borderColor="gray.300"
        bg="transparent"
        color="gray.600"
        fontWeight="normal"
        rightIcon={<CaretDown size={14} />}
        _hover={{ bg: "gray.50", borderColor: "gray.400" }}
        isDisabled={isDisabled}
        title={isDisabled ? disabledReason : undefined}
      >
        {selectedQuarter || "Quarter"}
      </MenuButton>
      <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1}>
        <MenuItem onClick={() => setQuarter(null)} fontWeight={!selectedQuarter ? "bold" : "normal"}>
          All quarters
        </MenuItem>
        {quarters.map((q) => (
          <MenuItem key={q} onClick={() => setQuarter(q)} fontWeight={selectedQuarter === q ? "bold" : "normal"}>
            {q}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

export const YearDropdown = ({ setYear, selectedYear }) => {
  // 2 years forward + current + 5 years back (descending)
  const years = Array.from({ length: 8 }, (_, i) => new Date().getFullYear() + 2 - i);

  return (
    <Menu>
      <MenuButton
        as={Button}
        size="sm"
        borderRadius="full"
        border="1px solid"
        borderColor="gray.300"
        bg="transparent"
        color="gray.600"
        fontWeight="normal"
        rightIcon={<CaretDown size={14} />}
        _hover={{ bg: "gray.50", borderColor: "gray.400" }}
      >
        {selectedYear || "Select year"}
      </MenuButton>
      <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1}>
        {years.map((year) => (
          <MenuItem key={year} onClick={() => setYear(year)} fontWeight={selectedYear === year ? "bold" : "normal"}>
            {year}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

export const DimensionDropdown = ({ selected, setSelected }) => {
  /**
   * Staging have a very large number of items in each dimension over 1.7k which makes the page to hang.
   * So we are limiting the number of items displayed per dimension to 10 by adidng pagination.
   * @type {number}
   */
  const PAGE_SIZE = 10;

  const toast = useToast();

  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState(search);
  const [pageByDimension, setPageByDimension] = useState({});

  const selectedCount = Object.values(selected).reduce((acc, arr) => acc + (arr?.length || 0), 0);

  // CSV per level, sorted, empty levels omitted — so equal selections produce
  // an identical react-query key regardless of tick order, and an empty
  // selection sends no params (backend then returns every item).
  const filterParams = useMemo(() => {
    const params = {};
    dimensionsKeys.forEach((key) => {
      // Drop non-numeric ids (hand-edited URLs) — a NaN in the CSV would 400
      // the request; the prune effect then clears them from the selection.
      const ids = (selected[key] || []).map(Number).filter((id) => !Number.isNaN(id));
      if (ids.length) {
        params[key] = ids.sort((a, b) => a - b).join(",");
      }
    });
    return params;
  }, [selected]);

  // Cascade: each level's options are the items still consistent with the
  // selections at the other levels (union within a level, bidirectional).
  const { isLoading, data: filterOptions } = useGetDimensionFilterOptions(filterParams, {
    keepPreviousData: true, // don't flash empty option lists between ticks
    cacheTime: 60 * 1000,
    onError: (error) => {
      console.error("Error fetching dimension filter options:", error);
      toast({
        title: "Error fetching dimension options",
        description: error?.response?.data?.parameters?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });
  const dimensionsStructure = filterOptions || {};

  // Auto-clear selections that stopped being valid options (e.g. a ticked
  // department whose country was just unticked). Depends on the fetched data
  // only: pruning shrinks `selected`, which triggers one refetch, and since a
  // smaller selection never invalidates surviving items the next pass is a
  // no-op — so this converges instead of looping.
  useEffect(() => {
    if (!filterOptions) return;
    let changed = false;
    const pruned = {};
    dimensionsKeys.forEach((key) => {
      const current = selected[key] || [];
      if (current.length === 0) return;
      // String-compare: URL-seeded selections are strings, API ids are numbers.
      const validIds = new Set((filterOptions[key]?.items || []).map((item) => String(item.id)));
      const kept = current.filter((id) => validIds.has(String(id)));
      if (kept.length !== current.length) {
        changed = true;
        pruned[key] = kept;
      }
    });
    if (changed) setSelected((prev) => ({ ...prev, ...pruned }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions]);

  const toggleItem = (dimensionKey, itemId) => {
    setSelected((prev) => {
      const current = prev[dimensionKey] || [];
      const exists = current.includes(itemId);
      return {
        ...prev,
        [dimensionKey]: exists ? current.filter((id) => id !== itemId) : [...current, itemId],
      };
    });
  };

  const handleClearAll = () => {
    const cleared = {};
    dimensionsKeys.forEach((key) => {
      cleared[key] = [];
    });
    setSelected(cleared);
  };

  const handlePageChange = (dimensionKey, newPage) => {
    setPageByDimension((prev) => ({
      ...prev,
      [dimensionKey]: newPage,
    }));
  };

  // Wait for user to stop typing before updating search
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(inputValue);
    }, 1000); // debounce

    return () => clearTimeout(handler);
  }, [inputValue, setSearch]);

  return (
    <Menu closeOnSelect={false}>
      {isLoading ? (
        <LocalSpinner />
      ) : (
        <MenuButton
          as={Button}
          size="sm"
          borderRadius="full"
          border="1px solid"
          borderColor={selectedCount > 0 ? "lime" : "gray.300"}
          bg={selectedCount > 0 ? "lightLime" : "transparent"}
          color={selectedCount > 0 ? "limeShade.600" : "gray.600"}
          fontWeight={selectedCount > 0 ? "600" : "normal"}
          rightIcon={<CaretDown size={14} />}
          _hover={{ bg: selectedCount > 0 ? "lightLime" : "gray.50", borderColor: selectedCount > 0 ? "lime" : "gray.400" }}
        >
          {selectedCount > 0 ? `Dimension (${selectedCount})` : "Dimension"}
        </MenuButton>
      )}

      <MenuList maxHeight="400px" overflowY="auto" borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1}>
        <Box px={3} py={2}>
          <Input
            type="text"
            placeholder="Search..."
            value={inputValue}
            // onChange={e => setSearch(e.target.value)}
            onChange={(e) => setInputValue(e.target.value)}
            w="100%"
            p="4px"
            size="sm"
            border="none"
          />
        </Box>
        {selectedCount > 0 && (
          <MenuItem color="status.red" fontSize="body.xxs" px={3} py={2} onClick={handleClearAll}>
            Clear All
          </MenuItem>
        )}
        {Object.entries(dimensionsStructure).map(([dimensionKey, dimension]) => {
          if (!dimension) return null;

          // Filter items by search
          const filteredItems = dimension.items.filter((item) =>
            item.name.toLowerCase().includes(search.toLowerCase())
          );
          const page = pageByDimension[dimensionKey] || 0;
          const totalPages = Math.ceil(filteredItems.length / PAGE_SIZE);

          const paginatedItems = filteredItems.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

          return (
            <Box key={dimensionKey}>
              <Box px={3} pt={2} pb={1} fontWeight="bold">
                {(dimension.name || "").toUpperCase()}
              </Box>
              {filteredItems.length === 0 ? (
                <MenuItem disabled>No items</MenuItem>
              ) : (
                <>
                  {paginatedItems.map((item) => (
                    <MenuItem key={item.id} onClick={() => toggleItem(dimensionKey, item.id)}>
                      <HStack justifyContent="space-between" w="full">
                        <Text fontWeight={selected[dimensionKey]?.includes(item.id) ? "bold" : "normal"}>
                          {item.name}
                        </Text>
                        {selected[dimensionKey]?.includes(item.id) ? (
                          <Check size={16} weight="bold" color="#82D909" />
                        ) : null}
                      </HStack>
                    </MenuItem>
                  ))}
                  {totalPages > 1 && (
                    <HStack px={3} py={2} justifyContent="space-between">
                      <Button
                        size="xs"
                        onClick={() => handlePageChange(dimensionKey, page - 1)}
                        isDisabled={page === 0}
                      >
                        Prev
                      </Button>
                      <Text fontSize="xs">
                        Page {page + 1} of {totalPages}
                      </Text>
                      <Button
                        size="xs"
                        onClick={() => handlePageChange(dimensionKey, page + 1)}
                        isDisabled={page + 1 >= totalPages}
                      >
                        Next
                      </Button>
                    </HStack>
                  )}
                </>
              )}
              <Divider my={1} />
            </Box>
          );
        })}
      </MenuList>
    </Menu>
  );
};

export default FootPrint;
